import {
  createEntityAdapter,
  createSlice,
  EntityState,
  PayloadAction,
} from '@reduxjs/toolkit';
import type { RootState } from 'store';
import { OriginalMenu } from 'types/menu';
import { MenuCategory } from 'types/menuCategory';

const menuCategoriesAdapter = createEntityAdapter<MenuCategory>({
  sortComparer: (a, b) => a.displayOrder - b.displayOrder,
});

const menusAdapter = createEntityAdapter<OriginalMenu>({
  sortComparer: (a, b) => a.displayOrder - b.displayOrder,
});

/**
 * メニューカテゴリー一覧画面の状態
 */
interface ListState {
  /** ロード中フラグ */
  loading: boolean;
  /** メニューカテゴリー一覧 */
  menuCategories: EntityState<MenuCategory>;
  /** メニュー一覧 */
  menus: EntityState<OriginalMenu>;
}

/**
 * メニューカテゴリー一覧画面の初期状態
 */
const initialState: ListState = {
  loading: false,
  menuCategories: menuCategoriesAdapter.getInitialState(),
  menus: menusAdapter.getInitialState(),
};

/**
 * メニューカテゴリーー画面の slice(reducer + action)
 */
export const listSlice = createSlice({
  name: 'listMenuCategory',
  initialState,
  reducers: {
    init: () => initialState,
    loadStarted: (state) => {
      state.loading = true;
    },
    loadEnded: (state) => {
      state.loading = false;
    },
    fetchMenuCategoriesSuccess: (
      state,
      action: PayloadAction<{
        menuCategories: MenuCategory[];
      }>,
    ) => {
      const { menuCategories } = action.payload;
      menuCategoriesAdapter.setAll(state.menuCategories, menuCategories);
    },
    fetchMenusRelatedToCategorySuccess: (
      state,
      action: PayloadAction<{
        menus: OriginalMenu[];
      }>,
    ) => {
      menusAdapter.upsertMany(state.menus, action.payload.menus);
    },
    updateMenusRelatedToCategorySuccess: (
      state,
      action: PayloadAction<{
        menus: {
          id: number;
          changes: OriginalMenu;
        }[];
      }>,
    ) => {
      menusAdapter.updateMany(state.menus, action.payload.menus);
    },
    deleteMenuSuccess: (
      state,
      action: PayloadAction<{
        menuId: number;
      }>,
    ) => {
      menusAdapter.removeOne(state.menus, action.payload.menuId);
    },
  },
});

/**
 * メニューカテゴリー一覧画面の action
 */
export const listActions = listSlice.actions;

/**
 * メニューカテゴリー一覧画面の selector（内部用）
 */
const getloading = (state: RootState) => state.menuCategory.list.loading;

const { selectAll: selectAllMenuCategories } =
  menuCategoriesAdapter.getSelectors<RootState>(
    (state) => state.menuCategory.list.menuCategories,
  );
const getMenuCategories = (state: RootState) => selectAllMenuCategories(state);

const { selectAll: selectAllMenus } = menusAdapter.getSelectors<RootState>(
  (state) => state.menuCategory.list.menus,
);
const getMenus = (state: RootState) => (categoryId: number) =>
  selectAllMenus(state).filter((menu) => menu.categoryId === categoryId);

/**
 * メニューカテゴリー一覧画面の selector
 */
export const listSelectors = {
  loading: getloading,
  filteredMenuCategories: getMenuCategories,
  getMenus,
} as const;
